import { SearchOutlined } from "@ant-design/icons";
import { COLORS } from "@coeff/utils";
import {
  Input as InputAD,
  InputProps as InputPropsAD,
  InputNumber as InputNumberAD,
  DatePicker,
} from "antd";
import { TextAreaProps } from "antd/lib/input";
import clsx from "clsx";
import React from "react";
import styled from "styled-components";

const { TextArea } = InputAD;

const StyledInput = styled.div<{ noMargin?: boolean }>`
  position: relative;
  display: flex;
  padding: 0 8px;
  margin: ${props => (props.noMargin ? "0px" : "0 8px;")};
  background: #fff;
  border: 2px solid white;
  border-radius: 4px;
  font-weight: 500;

  .ant-input {
    flex: 1;
    border: none;
    box-shadow: none;
    background: none;
    padding: 0;
    margin: 0;

    /* Hide number input spinners */
    &[type="number"]::-webkit-outer-spin-button,
    &[type="number"]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[type="number"] {
      -moz-appearance: textfield;
    }
  }

  &.cellpicker {
    border-color: ${COLORS.green} !important;
    opacity: 1;
  }

  &:focus-within {
    border-color: ${COLORS.coeblue4}!important;
  }

  &:hover {
    border-color: ${COLORS.black15};

    &:focus-within {
      border-color: ${COLORS.coeblue4}!important;
    }

    &.error {
      border: 2px solid ${COLORS.red} !important;
    }
  }

  &.disabled {
    background-color: ${COLORS.black5} !important;
  }

  &.error {
    border: 2px solid ${COLORS.red} !important;
  }
`;

const StyledInputSearch = styled.div<{ noMargin?: boolean }>`
  position: relative;
  display: flex;
  padding: 5px 8px;
  margin: ${props => (props.noMargin ? "none" : "5px 4px")};
  background: ${COLORS.black4};
  border: 2px solid ${COLORS.black4};
  border-radius: 4px;
  font-weight: 500;

  &:focus-within {
    border-color: ${COLORS.coeblue4};
  }

  // The whole box reads as a text field, so it takes the caret everywhere -- including
  // the wrapper's padding, which is not part of any input element (see InputSearch).
  cursor: text;

  .ant-input-affix-wrapper {
    background: none;
    border: none;
    box-shadow: none;
    // Fill the content box rather than sizing to the text, so the space to the right of
    // a short query is live: antd already routes clicks there into the input.
    flex: 1;
    // antd's own inset (0 7px) is deliberately kept: many call sites set a vertical-only
    // padding on .coeff__input-search and rely on it for their whole horizontal inset.
    // A call site that wants the box's padding to be the whole inset zeroes the
    // .ant-input-affix-wrapper padding itself, as ChooseObjectForm does.
  }

  .ant-input {
    flex: 1;
    border: none;
    box-shadow: none;
    background: none;
    padding: 0;
    margin: 0;
  }

  &:hover {
    border-color: ${COLORS.black15};
  }

  &:focus-within {
    border-color: ${COLORS.coeblue4};
  }
`;

const StyledTextarea = styled.div`
  position: relative;
  display: flex;
  padding: 4px;
  background: #fff;
  border: 2px solid white;
  border-radius: 4px;

  &:focus-within {
    border-color: ${COLORS.coeblue4};
    color: inherit;
  }

  &.error {
    border-color: ${COLORS.red} !important;
  }

  .ant-input {
    color: ${COLORS.black85};
    font-weight: 500;
    font-size: 16px;
    line-height: 1.25;
    flex: 1;
    border: none;
    box-shadow: none;
    background: none;
    padding: 0;
    margin: 0;
    resize: none;
  }

  &:hover {
    border-color: ${COLORS.black15};

    &:focus-within {
      border-color: ${COLORS.coeblue4};
      color: inherit;
    }
  }
`;

const CoeffBorderedInput = styled(InputAD).withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    String(prop) !== "error" && defaultValidatorFn(prop),
})`
  && {
    border-radius: 4px;
    border: 2px solid ${COLORS.black15}!important;
    box-shadow: none !important;
    transition: none;

    &:hover {
      box-shadow: none !important;
      border-color: ${COLORS.black15};
    }

    &:focus {
      border-color: ${COLORS.coeblue4}!important;
    }

    &.error {
      border-color: ${COLORS.red} !important;
    }
  }
`;

const CoeffBorderedTextArea = styled(TextArea)`
  && {
    border-radius: 4px;
    border: 2px solid ${COLORS.black15}!important;
    box-shadow: none !important;
    transition: none;

    &:hover {
      box-shadow: none !important;
      border-color: ${COLORS.black15};
    }

    &:focus {
      border-color: ${COLORS.coeblue4}!important;
    }

    &.error {
      border-color: ${COLORS.red} !important;
    }
  }
`;

const CoeffInputNumber = styled(InputNumberAD)`
  width: 100%;
  border-radius: 4px;
  border: 2px solid ${COLORS.black15}!important;
  box-shadow: none !important;
  transition: none;

  ${({ controls }) => {
    if (controls === false) {
      return `
        .ant-input-number-handler-wrap {
          display: none;
        }
      `;
    } else {
      return "";
    }
  }}

  &:hover {
    box-shadow: none !important;
    border-color: ${COLORS.black15};
  }

  &:focus {
    border-color: ${COLORS.coeblue4}!important;
  }

  &.error {
    border-color: ${COLORS.red} !important;
  }
`;

export interface InputProps extends InputPropsAD {
  error?: boolean;
  picker?: boolean;
  inputRef?: React.Ref<HTMLInputElement | any> | null;
  noMargin?: boolean;
}

export interface InputSearchProps extends InputPropsAD {
  inputRef?: React.Ref<HTMLInputElement | any> | null;
  noMargin?: boolean;
}

export interface InputTextareaProps extends TextAreaProps {
  error?: boolean;
  inputRef?: React.Ref<HTMLTextAreaElement | any> | null;
}

export const Input: React.FC<InputProps> = ({
  children,
  error = false,
  picker = false,
  noMargin,
  ...props
}) => (
  // using `cellpicker` insteadof `picker`, as it is the same classname as google file picker and we have added some overrides to it.
  <StyledInput
    noMargin={noMargin}
    className={`coeff__input${error ? " error" : ""}${picker ? " cellpicker" : ""}${
      props.disabled ? " disabled" : ""
    }`}
  >
    <InputAD
      data-1p-ignore
      size={props.size ? props.size : "small"}
      ref={props.inputRef}
      {...props}
    />
    {children}
  </StyledInput>
);

/**
 * The search box's padding belongs to the wrapper, not to the `input` inside it, so a
 * click that lands in it would otherwise hit a plain div and focus nothing. Forward
 * those clicks to the input; `preventDefault` keeps mousedown from blurring first.
 *
 * The padding cannot simply move onto the input: `prefix` and `allowClear` make antd
 * wrap the real `input` in `.ant-input-affix-wrapper` alongside the two icons, so the
 * inset has to be applied to something that contains all three. Roughly thirty call
 * sites already override `padding` on `.coeff__input-search`, which is why it still
 * lives on the outer element rather than on that inner wrapper.
 */
const focusInputFromPadding = (event: React.MouseEvent<HTMLDivElement>) => {
  if (event.target !== event.currentTarget) {
    return;
  }

  const input = event.currentTarget.querySelector("input");

  if (input) {
    event.preventDefault();
    input.focus();
  }
};

export const InputSearch = (props: InputSearchProps) => (
  <StyledInputSearch
    className={clsx(`coeff__input-search`, props.className)}
    noMargin
    onMouseDown={focusInputFromPadding}
  >
    <InputAD
      data-1p-ignore
      data-sentry-unmask
      size={props.size ? props.size : "small"}
      placeholder="Search"
      prefix={<SearchOutlined style={{ color: COLORS.black85 }} />}
      allowClear
      ref={props.inputRef}
      {...props}
    />
  </StyledInputSearch>
);

/**
 * Since `ref` is not a prop and cannot be passed to childred, I have added a dummy prop inputRef to receive the ref.
 * ForwardRef is not working for some reason
 *
 * TODO: make `ForwardRef` to work.
 */
export const InputTextarea = (props: InputTextareaProps) => {
  const { inputRef, autoSize: autoSize_, ...restProps } = props;
  let autoSize = autoSize_;
  if (autoSize === true) {
    // For autoSize=true, antd defaults to minRows = maxRows = 1. We want the default to be 2
    // max rows instead.
    autoSize = { minRows: 1, maxRows: 2 };
  }
  return (
    <StyledTextarea className={`coeff__input-textarea ${props.error ? "error" : ""}`}>
      <TextArea data-1p-ignore autoSize={autoSize} ref={inputRef} {...restProps} />
    </StyledTextarea>
  );
};

export const BorderedInput = CoeffBorderedInput;

export const BorderedTextArea = CoeffBorderedTextArea;

export const InputNumber = CoeffInputNumber;
